import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getHttpClient, HttpClient, useHttpClient } from '@bonusly/http-client';
import { getQueryClient } from '@bonusly/query-client';
import { Schema } from '@bonusly/schema';

import { Celebration, CelebrationDetail, CelebrationState, CelebrationType, CelebrationVisibility } from '../entities/celebrations';
import { ensureID, Metadata, PaginatedData, PaginationParams, RecordOrID } from './meta';
import { RuleCondition } from './nominations';

/*
 * Shared utilities
 */

enum QueryKey {
	Celebrations = 'celebrations:celebrations',
	Metadata = 'celebrations:metadata',
}

const headers = {
	'Key-Inflection': 'camel',
};

/*
 * Path and query key builders
 */

export const celebrationQueryKey = (id: string) => [QueryKey.Celebrations, id];
export const celebrationsQueryKey = (params?: Record<string, any>) => [QueryKey.Celebrations, params].filter(Boolean);
export const celebrationMetadataQueryKey = (params?: Record<string, any>) => [QueryKey.Metadata, params].filter(Boolean);

export const celebrationsPath = (prefix = '') => `${prefix}/admin/celebrations`;
export const celebrationPath = (id: string, prefix = '') => `${prefix}/admin/celebrations/${id}`;
export const celebrationMetadataPath = (prefix = '') => `${prefix}/admin/celebrations/metadata`;
export const giveCelebrationsPath = (prefix = '') => `${prefix}/admin/celebrations/manually_give`;

type CelebrationAPIData = {
	headline: string;
	iconUrl: string | null;
	id: string;
	imageUrl: string | null;
	reason: string;
	state: CelebrationState;
	type: CelebrationType;
	visibility: CelebrationVisibility;
	yearAmountMappings: { yearThreshold: number; amount: 0 }[];
};

/*
 * Index queries
 */

type CelebrationsIndexParams = PaginationParams<{
	state?: 'active' | 'inactive';
}>;

export const useCelebrations = (params?: CelebrationsIndexParams) => {
	const http = useHttpClient();
	const allParams = { state: 'active', ...params };

	return useQuery({
		queryKey: celebrationsQueryKey(allParams),
		queryFn: async () => {
			const res = await http.get<PaginatedData<CelebrationAPIData>>(celebrationsPath(), {
				params: allParams,
				headers,
			});

			return [Schema.deserialize(Celebration, res.data.records), new Metadata(res.data.metadata)] as [
				Celebration[],
				PaginatedData<CelebrationAPIData>['metadata'],
			];
		},
	});
};

export const createUnpaginatedCelebrationsFetcher = (params?: CelebrationsIndexParams) => async () => {
	const http = await getHttpClient();
	const res = await http.get<PaginatedData<CelebrationAPIData>>(celebrationsPath(), { params, headers });

	// Whenever we create a new celebration we first check to see if a deactivated one of that type exists. If it does, we reactivate it
	// with the newly provided data. Therefore, we should only ever have (under current circumstances) at most 4 inactive celebrations at a time.
	// Although this data is paginaged we can safely assume that all of the returned records will be included in the first page of data. We
	// do not need to include pagination metadata in the returned results.
	return Schema.deserialize(Celebration, res.data.records);
};

export const loadInactiveCelebrations = async () => {
	const queryClient = await getQueryClient();
	const params = { state: 'inactive' } as const;

	return queryClient.fetchQuery({
		queryKey: celebrationsQueryKey(params),
		queryFn: createUnpaginatedCelebrationsFetcher(params),
	});
};

export const useInactiveCelebrations = () => {
	const params = { state: 'inactive' } as const;

	return useQuery({
		queryKey: celebrationsQueryKey(params),
		queryFn: createUnpaginatedCelebrationsFetcher(params),
	});
};

type CelebrationIDMetadataParams = {
	celebration_id: string;
};

type CelebrationTypeMetadataParams = {
	celebration_type: string;
};

type CelebrationMetadataParams = CelebrationIDMetadataParams | CelebrationTypeMetadataParams;

export type CelebrationsMetadataAPIData = {
	restrictedConditions: Array<{
		celebrationId: string;
		condition: {
			attr: string;
			value: string;
		};
	}>;
};

const createCelebrationMetadataFetcher = (params: CelebrationMetadataParams) => async () => {
	const http = await getHttpClient();
	const res = await http.get<CelebrationsMetadataAPIData>(celebrationMetadataPath(), { headers, params });
	return res.data;
};

export const loadCelebrationMetadata = async (params: CelebrationMetadataParams) => {
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: celebrationMetadataQueryKey(params),
		queryFn: createCelebrationMetadataFetcher(params),
	});
};

export const useCelebrationTypeMetadata = (type: string) => {
	return useQuery({
		queryKey: celebrationMetadataQueryKey({ celebration_type: type }),
		queryFn: createCelebrationMetadataFetcher({ celebration_type: type }),
	});
};

export const useCelebrationRecordMetadata = (recordOrId: RecordOrID) => {
	const id = ensureID(recordOrId);

	return useQuery({
		queryKey: celebrationMetadataQueryKey({ celebration_id: id }),
		queryFn: createCelebrationMetadataFetcher({ celebration_id: id }),
	});
};

/*
 * Detail queries
 */

const createCelebrationFetcher = (http: HttpClient, id: string) => async () => {
	const res = await http.get<CelebrationAPIData>(celebrationPath(id), { headers });
	return Schema.deserialize(CelebrationDetail, res.data);
};

export const useCelebration = (id: string) => {
	return useQuery({
		queryKey: celebrationQueryKey(id),
		queryFn: createCelebrationFetcher(useHttpClient(), id),
	});
};

export const loadCelebration = async (id: string) => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: celebrationQueryKey(id),
		queryFn: createCelebrationFetcher(http, id),
	});
};

/*
 * Update mutations
 */

type MutateCelebrationOptions = {
	onError?: (error: unknown) => void;
	onMutate?: () => void;
	onSuccess?: (celebration: CelebrationDetail) => void;
};

export type UpdateCelebrationAPIData = {
	headline: string;
	icon?: File;
	image?: File;
	name: string;
	reason: string;
	visibility: CelebrationVisibility;
	yearAmountMappings?: { yearThreshold: number; amount: number }[];
	receivingRulesConditions?: RuleCondition[];
	state: 'active' | 'inactive';
};

export const useUpdateCelebration = (recordOrId: RecordOrID, options: MutateCelebrationOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreCelebration();
	const invalidate = useInvalidateCelebrations();

	return useMutation({
		mutationFn: async (data: UpdateCelebrationAPIData | FormData) => {
			options.onMutate?.();

			try {
				const res = await http.patch(celebrationPath(ensureID(recordOrId)), data, { headers });
				const record = Schema.deserialize(CelebrationDetail, res.data);
				store(record);
				invalidate();
				options.onSuccess?.(record);
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useDeactivateCelebration = (recordOrId: RecordOrID, options: MutateCelebrationOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreCelebration();
	const invalidate = useInvalidateCelebrations();

	return useMutation({
		mutationFn: async () => {
			try {
				options.onMutate?.();

				const res = await http.patch(celebrationPath(ensureID(recordOrId)), { state: 'inactive' }, { headers });
				const record = Schema.deserialize(CelebrationDetail, res.data);
				store(record);
				invalidate();
				options.onSuccess?.(record);
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Create mutations
 */

export type CreateCelebrationAPIData = {
	headline: string;
	icon?: File;
	image?: File;
	name: string;
	reason: string;
	visibility: CelebrationVisibility;
	yearAmountMappings?: { yearThreshold: number; amount: number }[];
	receivingRulesConditions?: RuleCondition[];
	type: CelebrationType;
};

export const useCreateCelebration = (options: MutateCelebrationOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreCelebration();
	const invalidate = useInvalidateCelebrations();

	return useMutation({
		mutationFn: async (data: CreateCelebrationAPIData | FormData) => {
			options.onMutate?.();

			try {
				const res = await http.post(celebrationsPath(), data, { headers });
				const record = Schema.deserialize(CelebrationDetail, res.data);
				store(record);
				invalidate();
				options.onSuccess?.(record);
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

type GiveCelebrationOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: () => void;
};

type GiveCelebrationAPIData = {
	celebrationId: string;
	userIds: string[];
};

export const useGiveCelebration = (options: GiveCelebrationOptions = {}) => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (data: GiveCelebrationAPIData) => {
			try {
				await http.post(giveCelebrationsPath(), data, { headers });
				options.onSuccess?.();
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Cache management
 */

export const useStoreCelebration = () => {
	const queryClient = useQueryClient();

	return useCallback((record: CelebrationDetail) => {
		queryClient.setQueryData<CelebrationDetail>(celebrationQueryKey(record.id), record);
	}, []);
};

export const useInvalidateCelebrations = () => {
	const client = useQueryClient();

	return useCallback(() => {
		client.invalidateQueries(celebrationsQueryKey());
		client.invalidateQueries(celebrationMetadataQueryKey());
	}, [client]);
};
